import type { ColumnFiltersState } from '@tanstack/react-table';

const arrayValue = (columnFilters: ColumnFiltersState, columnId: string): string[] =>
  (columnFilters.find(f => f.id === columnId)?.value as string[] | undefined) ?? [];

/**
 * `DataTableFilterPanel`/`DataTableActiveFilterChips` write straight to
 * `column.setFilterValue()`, which becomes a new `ColumnFiltersState` array
 * delivered through `onColumnFiltersChange`. Catalog's URL (`useFilter()`)
 * stays the single source of truth for filter state (see `filter.context.tsx`),
 * so this recovers which single option the user just checked/unchecked from
 * the before/after array-valued entry for each candidate column, to replay
 * as one `toggleFilterOption(columnId, value)` call — the toolkit's own
 * `toggleArrayFilterValue` always changes exactly one value per invocation,
 * so a value present in exactly one side of the diff is always sufficient.
 */
export function findToggledFilterValue(
  prev: ColumnFiltersState,
  next: ColumnFiltersState,
  columnIds: string[]
): { columnId: string; value: string } | null {
  for (const columnId of columnIds) {
    const prevValues = arrayValue(prev, columnId);
    const nextValues = arrayValue(next, columnId);
    const added = nextValues.find(value => !prevValues.includes(value));
    if (added) return { columnId, value: added };
    const removed = prevValues.find(value => !nextValues.includes(value));
    if (removed) return { columnId, value: removed };
  }
  return null;
}
